/**
 * Hook for managing Image Director agent conversations
 * Handles creating, fetching, and managing conversation history
 */

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  fetchConversations as dbFetchConversations,
  createConversation as dbCreateConversation,
  endConversation as dbEndConversation,
  fetchMessages as dbFetchMessages,
  insertMessage as dbInsertMessage,
} from "@/services/db/conversations";
import type { AgentConversation, AgentMessage } from "./types";

export function useAgentConversations() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<AgentConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all conversations for the user
  const fetchConversations = useCallback(async () => {
    if (!user) {
      setConversations([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await dbFetchConversations();

      setConversations(
        data.map((conv) => ({
          id: conv.id,
          userId: conv.user_id,
          createdAt: conv.created_at,
          endedAt: conv.ended_at,
          title: conv.title,
        }))
      );
      setError(null);
    } catch (err) {
      console.error("Error fetching conversations:", err);
      setError("Failed to fetch conversations");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Create a new conversation
  const createConversation = async (title?: string): Promise<string | null> => {
    if (!user) return null;

    try {
      const id = await dbCreateConversation(user.id, title);
      await fetchConversations();
      return id;
    } catch (err) {
      console.error("Error creating conversation:", err);
      return null;
    }
  };

  // End a conversation
  const endConversation = async (conversationId: string) => {
    if (!user) return;

    try {
      await dbEndConversation(conversationId);
      await fetchConversations();
    } catch (err) {
      console.error("Error ending conversation:", err);
    }
  };

  return {
    conversations,
    loading,
    error,
    createConversation,
    endConversation,
    refetch: fetchConversations,
  };
}

export function useAgentMessages(conversationId: string | null) {
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchMessages = useCallback(async () => {
    if (!conversationId) {
      setMessages([]);
      return;
    }

    try {
      setLoading(true);
      const data = await dbFetchMessages(conversationId);

      setMessages(
        data.map((msg) => ({
          id: msg.id,
          conversationId: msg.conversation_id,
          role: msg.role as "user" | "assistant",
          content: msg.content,
          imageUrl: msg.image_url,
          createdAt: msg.created_at,
        }))
      );
    } catch (err) {
      console.error("Error fetching messages:", err);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Add a message to the conversation
  const addMessage = async (
    role: "user" | "assistant",
    content: string,
    imageUrl?: string
  ): Promise<boolean> => {
    if (!conversationId) return false;

    try {
      await dbInsertMessage({
        conversation_id: conversationId,
        role,
        content,
        image_url: imageUrl || null,
      });

      await fetchMessages();
      return true;
    } catch (err) {
      console.error("Error adding message:", err);
      return false;
    }
  };

  return {
    messages,
    loading,
    addMessage,
    refetch: fetchMessages,
  };
}

